import Box from '@mui/material/Box'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import React, { type ReactNode } from 'react'

import Button from './button'
import Dialog, { type DialogProps } from './dialog'
import DialogHeader from './dialog/header'

type BaseProps = {
  cancelButtonText?: string
  deleteButtonText?: string
  disabled?: boolean
  fullWidth?: boolean
  hideCancel?: boolean
  hideDeleteButton?: boolean
  icon?: string
  id?: string
  isOpen: boolean
  loading?: boolean
  maxWidth?: DialogProps['maxWidth']
  text: ReactNode
  title: ReactNode

  onClose: () => void
  onConfirm: () => void
}

type AdditionalButtonProps =
  | {
      additionalButtonText: string
      onAdditionalClick: () => void
    }
  | { additionalButtonText?: never; onAdditionalClick?: never }

type Props = BaseProps & AdditionalButtonProps

const ConfirmationDialog: React.FC<Props> = ({
  additionalButtonText,
  cancelButtonText = 'Cancel',
  deleteButtonText = 'Delete',
  disabled = false,
  fullWidth = false,
  hideCancel = false,
  hideDeleteButton = false,
  icon = 'warning',
  id = 'delete-dialog',
  isOpen,
  loading = false,
  maxWidth,
  text,
  title = 'Warning',
  onAdditionalClick,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog id={id} open={!!isOpen} fullWidth={fullWidth} maxWidth={maxWidth}>
      <DialogHeader closeButtonLabel="Close" icon={icon} title={title} onClose={onClose} />
      <DialogContent>
        <DialogContentText component={typeof text !== 'string' ? 'div' : 'p'}>{text}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        {additionalButtonText && (
          <Box sx={{ flex: 1 }}>
            <Button disabled={loading} sx={{ textDecoration: 'underline' }} onClick={onAdditionalClick}>
              {additionalButtonText}
            </Button>
          </Box>
        )}
        {!hideCancel && (
          <Button variant="outlined" id="confirmation-modal-cancel-btn" onClick={onClose}>
            {cancelButtonText}
          </Button>
        )}
        {!hideDeleteButton && (
          <Button
            variant="contained"
            color="primary"
            className="confirmation-modal-confirm-btn"
            onClick={onConfirm}
            disabled={disabled}
            loading={loading}
          >
            {deleteButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog
